import {
    AriaRangeCalendarProps,
    mergeProps,
    useCalendar,
    useCalendarCell,
    useCalendarGrid,
    useLocale,
    useRangeCalendar
} from 'react-aria';
import {
    CalendarState,
    CalendarStateOptions, RangeCalendarState,
    RangeCalendarStateOptions,
    useCalendarState,
    useRangeCalendarState
} from 'react-stately';
import {CalendarDate, createCalendar, getDayOfWeek, getWeeksInMonth, isSameDay} from '@internationalized/date';
import React, {ElementType, useRef} from "react";
import {useFocusRing} from "@react-aria/focus";
import {AriaButtonOptions, useButton} from "@react-aria/button";
import {ChevronLeftIcon, ChevronRightIcon} from "@heroicons/react/24/outline";
import {AriaCalendarProps, DateValue} from '@react-types/calendar';


export function RangeCalendar(props: RangeCalendarStateOptions<DateValue> | AriaRangeCalendarProps<DateValue>) {

    const {locale} = useLocale();
    const state = useRangeCalendarState({
        ...props,
        locale,
        // visibleDuration: { months: 2 },
        createCalendar,
    });

    const ref = useRef(null);
    const {calendarProps, prevButtonProps, nextButtonProps, title} =
        useRangeCalendar(props, state, ref);

    return (
        <div {...calendarProps} ref={ref} className="inline-block">
            <div className="flex items-center pb-4">
                <h2 className="flex-1 font-bold text-base ml-2">{title}</h2>
                <div className="flex items-center gap-2">
                    <CalendarButton {...prevButtonProps}>
                        <ChevronLeftIcon className="h-4 w-4"/>
                    </CalendarButton>
                    <CalendarButton {...nextButtonProps}>
                        <ChevronRightIcon className="h-4 w-4"/>
                    </CalendarButton>
                </div>
            </div>
            {/*<div className="flex items-center gap-8">*/}
            {/*    <CalendarGrid state={state} />*/}
            {/*    <CalendarGrid state={state} offset={{ months: 1 }} />*/}
            {/*</div>*/}
            <CalendarGrid state={state}/>
        </div>
    );
}


export function Calendar(props: CalendarStateOptions<DateValue> | AriaCalendarProps<DateValue>) {

    const {locale} = useLocale();
    const ref = useRef(null);
    const state = useCalendarState({createCalendar, ...props, locale});
    const {calendarProps, prevButtonProps, nextButtonProps, title} = useCalendar(props, state);

    return (
        <div {...calendarProps} ref={ref} className="inline-block text-gray-800">
            <div className="flex items-center pb-4">
                <h2 className="flex-1 font-bold text-base ml-2">{title}</h2>
                <div className="flex items-center gap-2">
                    <CalendarButton {...prevButtonProps}>
                        <ChevronLeftIcon className="h-4 w-4"/>
                    </CalendarButton>
                    <CalendarButton {...nextButtonProps}>
                        <ChevronRightIcon className="h-4 w-4"/>
                    </CalendarButton>
                </div>
            </div>
            <CalendarGrid state={state}/>
        </div>
    );
}


interface CalendarButtonProps extends AriaButtonOptions<ElementType> {
    children?: React.ReactNode;
}

function CalendarButton(props: CalendarButtonProps) {

    const ref = useRef<HTMLButtonElement | null>(null);
    const { buttonProps } = useButton(props, ref);
    const { focusProps, isFocusVisible } = useFocusRing();

    return (
        <button
            {...mergeProps(buttonProps, focusProps)}
            ref={ref}
            className={`p-2 rounded-full ${props.isDisabled ? "text-gray-400" : ""} ${
                !props.isDisabled ? "hover:bg-gray-100 active:bg-gray-200" : ""
            } outline-none ${
                isFocusVisible ? "ring-2 ring-offset-2 ring-gray-600" : ""
            }`}
        >
            {props.children}
        </button>
    );
}

// function CalendarGrid({state, ...props}) {
//
//     const {gridProps, headerProps, weekDays, weeksInMonth} = useCalendarGrid(props, state);
//
//     return (
//         <table {...gridProps}>
//             <thead {...headerProps}>
//             <tr>
//                 {weekDays.map((day, index) => <th key={index}>{day}</th>)}
//             </tr>
//             </thead>
//             <tbody>
//             {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
//                 <tr key={weekIndex}>
//                     {state.getDatesInWeek(weekIndex).map((date, i) => (
//                         date
//                             ? (
//                                 <CalendarCell
//                                     key={i}
//                                     state={state}
//                                     date={date}
//                                 />
//                             )
//                             : <td key={i}/>
//                     ))}
//                 </tr>
//             ))}
//             </tbody>
//         </table>
//     );
// }

export function CalendarGrid({ state, ...props }: { state: CalendarState | RangeCalendarState; }) {

    const { locale } = useLocale();
    const { gridProps, headerProps, weekDays } = useCalendarGrid(props, state);
    const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);

    return (
        <table {...gridProps} cellPadding="0" className="flex-1">
            <thead {...headerProps} className="text-gray-600">
            <tr>
                {weekDays.map((day, index) => (
                    <th className="text-center text-sm/6" key={index}>
                        {day}
                    </th>
                ))}
            </tr>
            </thead>
            <tbody>
            {Array.from({ length: weeksInMonth }, (_, weekIndex) => (
                <tr key={weekIndex}>
                    {state.getDatesInWeek(weekIndex).map((date, i) =>
                        date ? (
                            <CalendarCell key={i} state={state} date={date} />
                        ) : (
                            <td key={i} />
                        )
                    )}
                </tr>
            ))}
            </tbody>
        </table>
    );
}

// function CalendarCell({state, date}) {
//
//     const ref = useRef(null);
//     const {
//         cellProps,
//         buttonProps,
//         isSelected,
//         isOutsideVisibleRange,
//         isDisabled,
//         isUnavailable,
//         formattedDate
//     } = useCalendarCell({date}, state, ref);
//
//     return (
//         <td {...cellProps}>
//             <div
//                 {...buttonProps}
//                 ref={ref}
//                 hidden={isOutsideVisibleRange}
//                 className={`cell ${isSelected ? 'selected' : ''} ${
//                     isDisabled ? 'disabled' : ''
//                 } ${isUnavailable ? 'unavailable' : ''}`}
//             >
//                 {formattedDate}
//             </div>
//         </td>
//     );
// }


export function CalendarCell({state, date}: { state: CalendarState | RangeCalendarState; date: CalendarDate }) {

    const ref = useRef(null);
    const {
        cellProps,
        buttonProps,
        isSelected,
        isOutsideVisibleRange,
        isDisabled,
        formattedDate,
        isInvalid
    } = useCalendarCell({date}, state, ref);

    const hasHighlightedRange = (state: CalendarState | RangeCalendarState): state is RangeCalendarState => {
        return 'highlightedRange' in state;
    };

    // The start and end date of the selected range will have
    // an emphasized appearance.
    const isSelectionStart = hasHighlightedRange(state) && state.highlightedRange
        ? isSameDay(date, state.highlightedRange.start)
        : isSelected;

    const isSelectionEnd = hasHighlightedRange(state) && state.highlightedRange
        ? isSameDay(date, state.highlightedRange.end)
        : isSelected;

    // We add rounded corners on the left for the first day of the month,
    // the first day of each week, and the start date of the selection.
    // We add rounded corners on the right for the last day of the month,
    // the last day of each week, and the end date of the selection.
    const {locale} = useLocale();
    const dayOfWeek = getDayOfWeek(date, locale);
    const isRoundedLeft =
        isSelected && (isSelectionStart || dayOfWeek === 0 || date.day === 1);
    const isRoundedRight =
        isSelected &&
        (isSelectionEnd ||
            dayOfWeek === 6 ||
            date.day === date.calendar.getDaysInMonth(date));

    const {focusProps, isFocusVisible} = useFocusRing();

    return (
        <td
            {...cellProps}
            className={`text-sm/6 py-0.5 relative ${isFocusVisible ? "z-10" : "z-0"}`}
        >
            <div
                {...mergeProps(buttonProps, focusProps)}
                ref={ref}
                hidden={isOutsideVisibleRange}
                className={`w-8 h-8 outline-none group ${
                    isRoundedLeft ? "rounded-l-full" : ""
                } ${isRoundedRight ? "rounded-r-full" : ""} ${
                    isSelected ? (isInvalid ? "bg-red-300" : "bg-gray-300") : ""
                } ${isDisabled ? "disabled" : ""}`}
            >
                <div
                    className={`w-full h-full rounded-full flex items-center justify-center ${
                        isDisabled && !isInvalid ? "text-gray-400" : ""
                    } ${
                        // Focus ring, visible while the cell has keyboard focus.
                        isFocusVisible
                            ? "ring-2 group-focus:z-2 ring-gray-600 ring-offset-2"
                            : ""
                    } ${
                        // Darker selection background for the start and end.
                        isSelectionStart || isSelectionEnd
                            ? isInvalid
                                ? "bg-red-600 text-white hover:bg-red-700"
                                : "bg-gray-600 text-white hover:bg-gray-700"
                            : ""
                    } ${
                        // Hover state for cells in the middle of the range.
                        isSelected && !isDisabled && !(isSelectionStart || isSelectionEnd)
                            ? isInvalid
                                ? "hover:bg-red-400"
                                : "hover:bg-gray-400"
                            : ""
                    } ${
                        // Hover state for non-selected cells.
                        !isSelected && !isDisabled ? "hover:bg-gray-100" : ""
                    } cursor-default`}
                >
                    {formattedDate}
                </div>
            </div>
        </td>
    );
}